import type {
  CorridorAmountLimit,
  LimitPayerType,
} from "@/shared/services/payments/paymentCorridors.service";
import { formatCurrencyAmount } from "@/shared/utils/currencyMinorUnits";
import { compareDecimalStrings, isDecimalString } from "@/shared/utils/decimal";

/** Decimal strings; null = no bound on that side. */
export interface AmountBounds {
  min_amount: string | null;
  max_amount: string | null;
}

export interface CorridorLimits {
  person: AmountBounds;
  company: AmountBounds;
  /** Widest envelope of both kinds — what applies while the payer kind is not known yet. */
  any: AmountBounds;
  /** True when the two kinds differ on either side, i.e. the hint should show both. */
  differs: boolean;
}

export type AmountLimitViolation = "below_minimum" | "above_maximum";

export const AMOUNT_LIMIT_CODES = ["amount_above_maximum", "amount_below_minimum"] as const;
export type AmountLimitCode = (typeof AMOUNT_LIMIT_CODES)[number];

const UNBOUNDED: AmountBounds = { min_amount: null, max_amount: null };

const isBounded = (bounds: AmountBounds): boolean => bounds.min_amount !== null || bounds.max_amount !== null;

const sameBound = (a: string | null, b: string | null): boolean =>
  a === null || b === null ? a === b : compareDecimalStrings(a, b) === 0;

const boundsOf = (rows: CorridorAmountLimit[], payerType: LimitPayerType): AmountBounds => {
  const row = rows.find((entry) => entry.payer_type === payerType);
  return row ? { min_amount: row.min_amount ?? null, max_amount: row.max_amount ?? null } : UNBOUNDED;
};

// A null on either kind means that side is unbounded, so the envelope is too.
const widest = (a: AmountBounds, b: AmountBounds): AmountBounds => ({
  min_amount:
    a.min_amount === null || b.min_amount === null
      ? null
      : compareDecimalStrings(a.min_amount, b.min_amount) <= 0
        ? a.min_amount
        : b.min_amount,
  max_amount:
    a.max_amount === null || b.max_amount === null
      ? null
      : compareDecimalStrings(a.max_amount, b.max_amount) >= 0
        ? a.max_amount
        : b.max_amount,
});

/** Null when the corridor carries no limits at all — the caller shows no hint and caps nothing. */
export function corridorLimitsOf(limits: CorridorAmountLimit[] | null | undefined): CorridorLimits | null {
  if (!limits?.length) return null;
  const person = boundsOf(limits, "PERSON");
  const company = boundsOf(limits, "COMPANY");
  if (!isBounded(person) && !isBounded(company)) return null;
  return {
    person,
    company,
    any: widest(person, company),
    differs:
      !sameBound(person.min_amount, company.min_amount) || !sameBound(person.max_amount, company.max_amount),
  };
}

/** Exact bounds for a known payer kind; the envelope while it is unknown. Null when nothing applies. */
export function boundsForPayer(
  limits: CorridorLimits | null,
  payerType: LimitPayerType | null | undefined,
): AmountBounds | null {
  if (!limits) return null;
  const bounds =
    payerType === "PERSON" ? limits.person : payerType === "COMPANY" ? limits.company : limits.any;
  return isBounded(bounds) ? bounds : null;
}

/** Bounds are inclusive; a malformed amount is "nothing to check yet", not a violation. */
export function resolveAmountLimitViolation(
  amount: string | null | undefined,
  bounds: AmountBounds | null,
): AmountLimitViolation | null {
  if (!bounds || !amount || !isDecimalString(amount)) return null;
  if (bounds.max_amount !== null && compareDecimalStrings(amount, bounds.max_amount) > 0)
    return "above_maximum";
  if (bounds.min_amount !== null && compareDecimalStrings(amount, bounds.min_amount) < 0)
    return "below_minimum";
  return null;
}

export function isAmountLimitCode(code: string | null | undefined): code is AmountLimitCode {
  return (AMOUNT_LIMIT_CODES as readonly string[]).includes(code ?? "");
}

export function formatLimitAmount(value: string, currency: string, locale: string): string {
  return `${formatCurrencyAmount(value, currency, locale)} ${currency}`;
}

/**
 * Mirrors the backend's inference. PSE carries an explicit person kind (`personType`), which the network
 * takes at face value; the other Colombian rails only have the document, where a NIT identifies a company.
 */
export function payerTypeForColombianPayer(payer: {
  personType?: "natural" | "juridica";
  documentType?: string;
}): LimitPayerType {
  if (payer.personType) return payer.personType === "juridica" ? "COMPANY" : "PERSON";
  return payer.documentType?.trim().toUpperCase() === "NIT" ? "COMPANY" : "PERSON";
}
